import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getStableAnimalEmoji } from "../utils/utils";
import VenmoBadge from "./VenmoBadge";
import "./GroupFirstTimeInstructionsModal.css";

function MiniPersonRow({ seed, name, right }) {
  const emoji = useMemo(() => getStableAnimalEmoji(`fti:${seed}:${name}`), [seed, name]);
  return (
    <div className="gfti-mini-person d-flex align-items-center justify-content-between">
      <div className="d-flex align-items-center gap-2">
        <span className="gfti-mini-emoji" aria-hidden="true">
          {emoji}
        </span>
        <span className="gfti-mini-name">{name}</span>
      </div>
      <div className="gfti-mini-right">{right}</div>
    </div>
  );
}

export default function GroupFirstTimeInstructionsModal(props) {
  const { open, onDismiss, groupId, groupName } = props;
  const { t } = useTranslation();

  const seed = groupId || groupName || "animalsplit";
  const mascot = useMemo(() => getStableAnimalEmoji(`group-fti:${seed}`), [seed]);
  const fabMountRef = useRef(null);
  const contributorFabMountRef = useRef(null);

  useEffect(() => {
    // Reuse the exact DOM/button styles of the real Transactions FAB
    // (so the mock matches 1:1 even if global styles change).
    if (!open) return;
    const mount = fabMountRef.current;
    const contributorMount = contributorFabMountRef.current;
    if (!mount && !contributorMount) return;

    const cloneFabInto = (sourceId, targetEl) => {
      if (!targetEl) return;
      targetEl.innerHTML = "";
      const source = document.getElementById(sourceId);
      if (!source) return;
      const clone = source.cloneNode(true);
      try {
        clone.removeAttribute("id");
        clone.removeAttribute("data-bs-toggle");
        clone.removeAttribute("data-bs-target");
        clone.setAttribute("aria-hidden", "true");
        clone.setAttribute("tabindex", "-1");
        // Ensure it can't be interacted with in the instructions modal, while keeping
        // the visuals identical to the real (enabled) FAB.
        clone.style.pointerEvents = "none";
        // Avoid inheriting "invisible/visible" toggles from the live UI.
        clone.classList.remove("invisible");
        clone.classList.remove("visible");
        clone.disabled = true;
      } catch (e) {
        // ignore DOM mutation failures
      }
      targetEl.appendChild(clone);
    };

    cloneFabInto("create-transaction-fab", mount);
    cloneFabInto("create-contributor-fab", contributorMount);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="group-first-time-instructions-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t("group.instructions.title")}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onDismiss?.();
      }}
    >
      <div className="group-first-time-instructions-box as-modal as-theme">
        <div className="modal-header">
          <div className="as-modal-header-row">
            <div className="as-modal-title-row">
              <span className="as-modal-icon-badge" aria-hidden="true">
                🐾
              </span>
              <div className="gfti-title-stack">
                <h5 className="modal-title mb-0">{t("group.instructions.title")}</h5>
                <div className="small text-muted">
                  {t("group.instructions.subtitle", { groupName: groupName || "" })}
                </div>
              </div>
            </div>
            <div className="as-modal-actions">
              <div className="gfti-mascot" aria-hidden="true">
                {mascot}
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={() => onDismiss?.()}
                aria-label={t("general.close")}
                title={t("general.close")}
              />
            </div>
          </div>
        </div>

        <div className="modal-body">
          <div className="gfti-stickers" aria-hidden="true">
            <span>🐶</span>
            <span>🐱</span>
            <span>🐰</span>
            <span>🦊</span>
            <span>🐻</span>
          </div>

          <div className="gfti-mock-grid">
            <div className="gfti-mock-card card">
              <div className="card-body">
                <div className="gfti-mock-tag">🧾 {t("group.instructions.create_txn_title")}</div>
                <div className="gfti-mock-title">{t("group.instructions.create_txn_subtitle")}</div>
                <div className="gfti-mock-subtitle">{t("group.instructions.create_txn_body")}</div>

                <div className="gfti-mini-panel">
                  <div className="gfti-txn-card-mock position-relative">
                    <div className="gfti-txn-card-header d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                        <span className="as-icon-badge" aria-hidden="true">
                          🧾
                        </span>
                        <strong className="truncate">{t("transaction.title")}</strong>
                      </div>
                      <span className="gfti-txn-card-chevron" aria-hidden="true">
                        <i className="bi bi-chevron-up" />
                      </span>
                    </div>
                    <div className="gfti-txn-card-body" aria-hidden="true" />
                    <span ref={fabMountRef} className="gfti-fab-mount" aria-hidden="true" />
                  </div>

                  <div className="gfti-txn-card-mock position-relative mt-3">
                    <div className="gfti-txn-card-header d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                        <span className="as-icon-badge" aria-hidden="true">
                          🐾
                        </span>
                        <strong className="truncate">{t("participant.title")}</strong>
                      </div>
                      <span className="gfti-txn-card-chevron" aria-hidden="true">
                        <i className="bi bi-chevron-up" />
                      </span>
                    </div>
                    <div className="gfti-txn-card-body" aria-hidden="true" style={{ height: 8 }} />
                    <div className="px-3 pb-3" aria-hidden="true">
                      <MiniPersonRow seed={seed} name="User A" right={<span />} />
                      <MiniPersonRow seed={seed} name="User B" right={<span />} />
                    </div>
                    <span ref={contributorFabMountRef} className="gfti-fab-mount" aria-hidden="true" />
                  </div>

                  <ol className="gfti-steps">
                    <li>{t("group.instructions.create_txn_step_1")}</li>
                    <li>{t("group.instructions.create_txn_step_2")}</li>
                    <li>{t("group.instructions.create_txn_step_3")}</li>
                  </ol>

                  <div className="gfti-mini-subheader">{t("transaction.amount")} + {t("transaction.contributor")}</div>
                  <div className="gfti-mock-two-col">
                    <div className="gfti-mock-field">
                      <div className="gfti-mock-field-label">{t("transaction.amount")}</div>
                      <div className="gfti-mock-field-control">$48.00</div>
                    </div>
                    <div className="gfti-mock-field">
                      <div className="gfti-mock-field-label">{t("transaction.contributor")}</div>
                      <div className="gfti-mock-field-control gfti-mock-select">
                        <span className="gfti-mini-emoji" aria-hidden="true">
                          {getStableAnimalEmoji(`fti:${seed}:payer`)}
                        </span>
                        <span className="gfti-mini-name">User A</span>
                        <span className="gfti-mock-caret" aria-hidden="true">▾</span>
                      </div>
                    </div>
                  </div>

                  <div className="gfti-mini-subheader">{t("transaction.split_by")}</div>
                  <div className="d-flex align-items-center justify-content-end gap-2">
                    <span className="gfti-pill">🍃 {t("transaction.shares")}</span>
                    <span className="gfti-pill">🥤 {t("transaction.exact_amounts")}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="gfti-mock-card card">
              <div className="card-body">
                <div className="gfti-mock-tag">🧮 {t("group.instructions.split_title")}</div>
                <div className="gfti-mock-title">{t("group.instructions.split_subtitle")}</div>
                <div className="gfti-mock-subtitle">{t("group.instructions.split_body")}</div>

                <div className="gfti-mini-panel">
                  <div className="gfti-mini-panel-title">🍃 {t("group.instructions.shares_title")}</div>
                  <div className="gfti-mini-panel-subtitle">{t("group.instructions.shares_example_label")}</div>
                  <MiniPersonRow seed={seed} name="User A" right={<span className="gfti-chip">2</span>} />
                  <MiniPersonRow
                    seed={seed}
                    name="User B"
                    right={
                      <span className="gfti-chip gfti-chip--muted" title={t("group.instructions.excluded")}>
                        0
                      </span>
                    }
                  />
                  <MiniPersonRow seed={seed} name="User C" right={<span className="gfti-chip">1</span>} />
                  <div className="gfti-mini-hint">{t("group.instructions.shares_hint")}</div>

                  <div className="mt-3 pt-3" style={{ borderTop: "1px solid rgba(0, 0, 0, 0.08)" }}>
                    <div className="gfti-mini-panel-title">🥤 {t("group.instructions.exact_title")}</div>
                    <div className="gfti-mini-panel-subtitle">{t("group.instructions.exact_example_label")}</div>
                    <MiniPersonRow seed={seed} name="User A" right={<span className="gfti-chip">$10</span>} />
                    <MiniPersonRow seed={seed} name="User B" right={<span className="gfti-chip">$10</span>} />
                    <MiniPersonRow
                      seed={seed}
                      name="User C"
                      right={<span className="gfti-chip gfti-chip--muted">$80</span>}
                    />
                    <div className="gfti-mini-hint">{t("group.instructions.exact_hint")}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="gfti-mock-card card">
              <div className="card-body">
                <div className="gfti-mock-tag">🔒 {t("group.instructions.settle_title")}</div>
                <div className="gfti-mock-title">{t("group.instructions.settle_subtitle")}</div>
                <div className="gfti-mock-subtitle">{t("group.instructions.settle_body")}</div>

                <div className="gfti-mini-panel">
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <div className="gfti-mini-panel-title">{t("group.settle") || "Settle"}</div>
                    <span className="gfti-pill">🔒 {t("group.settled") || "Settled"}</span>
                  </div>
                  <ul className="gfti-steps">
                    <li>{t("group.instructions.settle_step_1")}</li>
                    <li>{t("group.instructions.settle_step_2")}</li>
                    <li>{t("group.instructions.settle_step_3")}</li>
                  </ul>
                  <div className="gfti-mini-hint">{t("group.instructions.settle_hint")}</div>
                </div>
              </div>
            </div>

            <div className="gfti-mock-card card">
              <div className="card-body">
                <div className="gfti-mock-tag">💸 {t("group.instructions.venmo_title")}</div>
                <div className="gfti-mock-title">{t("group.instructions.venmo_subtitle")}</div>
                <div className="gfti-mock-subtitle">{t("group.instructions.venmo_body")}</div>

                <div className="gfti-mini-panel">
                  <div className="gfti-mini-panel-title">👤 {t("participant.edit_participant")}</div>
                  <div className="gfti-mini-panel-subtitle">{t("participant.venmo_username")}</div>
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                      <span className="gfti-mini-emoji" aria-hidden="true">
                        {getStableAnimalEmoji(`fti:${seed}:venmo`)}
                      </span>
                      <span className="gfti-mini-name">Tom Cat</span>
                      <VenmoBadge username="Tom-Cat-11" className="ms-1" />
                    </div>
                  </div>
                  <div className="gfti-mini-hint">{t("group.instructions.venmo_hint")}</div>

                  <div className="gfti-mini-subheader">{t("group.instructions.venmo_pay_debt_title")}</div>
                  <div className="gfti-settlement-footer-mock" aria-hidden="true">
                    <button type="button" className="btn btn-sm as-btn-venmo gfti-venmo-cta" disabled>
                      {t("general.pay_with_venmo", { defaultValue: "Pay with Venmo" })}
                    </button>
                    <button type="button" className="btn btn-sm btn-success" disabled>
                      {t("general.mark_paid", { defaultValue: "Mark paid" })}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="gfti-tips">
            <div className="gfti-tips-title">{t("group.instructions.tips_title")}</div>
            <ul className="gfti-tips-list">
              <li>{t("group.instructions.tip_1")}</li>
              <li>{t("group.instructions.tip_2")}</li>
              <li>{t("group.instructions.tip_3")}</li>
            </ul>
          </div>
        </div>

        <div className="modal-footer as-modal-footer gfti-footer">
          <button type="button" className="btn as-btn-soft" onClick={() => onDismiss?.()}>
            {t("group.instructions.got_it")}
          </button>
        </div>
      </div>
    </div>
  );
}

